import { Link } from 'react-router'
import { Mail, Phone } from 'lucide-react'

import { categories } from '../../data/products'
import { instagramUrl, orderUrl, phoneUrl, useCatalog, whatsappUrl } from '../lib/catalog'
import { useI18n } from '../lib/i18n'
import { LanguageButtons, Logo } from './Nav'
import { TelegramIcon, TelegramOrderButton, WhatsAppIcon } from './TelegramButton'
import { Container } from './ui'

/** Brand marks are not part of the icon set, so this one is drawn inline. */
function Instagram({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

export function Footer() {
  const { t } = useI18n()
  const { settings } = useCatalog()
  const telegram = orderUrl(settings)
  const instagram = settings.instagram ? instagramUrl(settings.instagram) : null
  const whatsapp = settings.whatsapp ? whatsappUrl(settings.whatsapp) : null
  const phone = settings.phone ? phoneUrl(settings.phone) : null
  const email = settings.email ? `mailto:${settings.email}` : null

  return (
    <footer className="mt-20 border-t border-line sm:mt-28">
      <Container className="py-14 sm:py-16">
        <div className="grid gap-12 lg:grid-cols-[1.25fr_1fr_1fr] lg:gap-10">
          <div className="max-w-sm">
            <Logo />
            <p className="mt-4 text-[14px] leading-relaxed text-muted">
              {t('footer_description')}
            </p>
            <TelegramOrderButton label={t('contact_us')} size="md" className="mt-6" full={false} />
            {telegram && <p className="mt-3 text-[12.5px] text-faint">{telegram.replace('https://', '')}</p>}
          </div>

          <nav aria-label={t('site_sections')}>
            <p className="text-[12px] font-medium uppercase tracking-[0.14em] text-muted">{t('site_sections')}</p>
            <ul className="mt-4 flex flex-col gap-2.5">
              {[
                ['shop', '/shop'],
                ['categories', '/#kategoriyalar'],
                ['about', '/about'],
                ['contact', '/#aloqa'],
              ].map(([label, to]) => (
                <li key={label}>
                  <Link to={to} className="inline-flex min-h-11 items-center text-[14px] text-muted transition-colors hover:text-forest">
                    {t(label)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="text-[12px] font-medium uppercase tracking-[0.14em] text-muted">{t('categories')}</p>
            <ul className="mt-4 flex flex-col gap-2.5">
              {categories.slice(0, 4).map((c) => (
                <li key={c.key}>
                  <Link to={`/shop?kategoriya=${c.key}`} className="inline-flex min-h-11 items-center text-[14px] text-muted transition-colors hover:text-forest">
                    {t(`category_${c.key}`)}
                  </Link>
                </li>
              ))}
            </ul>

            <p className="mt-7 text-[12px] font-medium uppercase tracking-[0.14em] text-muted">{t('contact_us')}</p>
            <ul className="mt-4 flex flex-col gap-2.5">
              {phone && (
                <li>
                  <a href={phone} className="inline-flex min-h-11 items-center gap-2 text-[14px] text-muted transition-colors hover:text-forest">
                    <Phone className="size-4" />{settings.phone}
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a href={email} className="inline-flex min-h-11 items-center gap-2 text-[14px] text-muted transition-colors hover:text-forest">
                    <Mail className="size-4" />{settings.email}
                  </a>
                </li>
              )}
              {telegram && (
                <li>
                  <a href={telegram} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 text-[14px] text-muted transition-colors hover:text-forest">
                    <TelegramIcon className="size-4" />
                    {t('contact_us')}
                  </a>
                </li>
              )}
              {whatsapp && (
                <li>
                  <a href={whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 text-[14px] text-muted transition-colors hover:text-forest">
                    <WhatsAppIcon className="size-4" />
                    {t('whatsapp_contact')}
                  </a>
                </li>
              )}
              {instagram && (
                <li>
                  <a href={instagram} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 text-[14px] text-muted transition-colors hover:text-forest">
                    <Instagram className="size-4" />@{settings.instagram?.replace(/^@/, '')}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col-reverse items-start justify-between gap-4 border-t border-line pt-6 sm:flex-row sm:items-center">
          <p className="text-[12.5px] text-faint">
            © {new Date().getFullYear()} {t('brand')} {t('rights_reserved')}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/admin" className="inline-flex min-h-11 items-center text-[12.5px] text-muted transition-colors hover:text-forest">
              {t('admin_panel')}
            </Link>
            <span className="text-[12.5px] text-faint">{t('contact_us')}</span>
            <LanguageButtons className="hidden sm:flex lg:hidden" />
          </div>
        </div>
      </Container>
    </footer>
  )
}
